import Announcement from '@mui/icons-material/Announcement';
import NoteAdd from '@mui/icons-material/NoteAdd';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Dominios from '../util/Dominios';
import ReactComponent from './ReactComponent';
import ModalConfirmacao from './sessao/ModalConfirmacao';

class IconeRessalva extends ReactComponent {
    abrirDialog = false;
    ressalvas = [];
    abrirConfirmacaoAdicionarRascunhoRessalva = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            abrirDialog: observable,
            ressalvas: observable,
            abrirConfirmacaoAdicionarRascunhoRessalva: observable,
            handleCloseDialog: action,
            existeVotoComRessalva: computed,
            votosComRessalva: computed,
            tooltip: computed,
            handleClickIcone: action,
            handleClickAdicionarRascunho: action,
            fecharConfirmacaoTextoRessalva: action
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    handleCloseDialog = () => {
        this.abrirDialog = false;
    };

    get existeVotoComRessalva() {
        return (
            this.registro.votos &&
            this.registro.votos.findIndex((voto) => voto.voto?.codigo === Dominios.CODIGO_VOTO_RESSALVA) !== -1
        );
    }

    get votosComRessalva() {
        if (!this.registro.votos) {
            return [];
        }
        return this.registro.votos.reduce((acumulador, voto) => {
            if (
                voto.voto?.codigo === Dominios.CODIGO_VOTO_RESSALVA &&
                (voto.situacaoVoto === 'CONFIRMADO' || voto.situacaoVoto === 'PRE_VOTADO')
            ) {
                acumulador.push({
                    sigla: voto.siglaDoMagistrado,
                    situacaoVoto: voto.situacaoVoto
                });
            }
            return acumulador;
        }, []);
    }

    getDescricaoSituacaoVoto(situacaoVoto) {
        if (situacaoVoto === 'CONFIRMADO') {
            return 'Confirmado';
        }
        if (situacaoVoto === 'PRE_VOTADO') {
            return 'Pré-votado';
        }
    }

    get tooltip() {
        return (
            <div style={{ margin: '5px' }}>
                <div>Votação com ressalva</div>
                <ul style={{ listStyleType: 'circle' }}>
                    {this.votosComRessalva.map((voto) => (
                        <li key={this.nextKey}>
                            {voto.sigla + ' - ' + this.getDescricaoSituacaoVoto(voto.situacaoVoto)}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    handleClickIcone = async () => {
        this.abrirDialog = true;
        this.ressalvas = [];
        let votosConfirmados = this.votosComRessalva.filter((voto) => voto.situacaoVoto === 'CONFIRMADO');
        let promises = votosConfirmados.map((voto) =>
            this.processoService.recuperarObservacaoVoto(
                this.registro.sessaoId,
                this.registro.processo.numeroCompleto,
                Dominios.TIPO_OBSERVACAO_VOTO_RESSALVA,
                voto.sigla
            )
        );
        let respostas = await Promise.all(promises);
        let promisesConteudo = [];
        respostas.forEach((resposta) => {
            resposta.forEach((observacaoVoto) => {
                let promise = this.processoService.recuperarObservacaoVotoPeloId(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    Dominios.TIPO_OBSERVACAO_VOTO_RESSALVA,
                    observacaoVoto.magistrado.sigla,
                    observacaoVoto.id
                );
                promisesConteudo.push(promise);
            });
        });
        let respostasConteudo = await Promise.all(promisesConteudo);
        respostasConteudo.forEach((resposta) => {
            runInAction(() => this.ressalvas.push({ sigla: resposta.magistrado.sigla, conteudo: resposta.conteudo }));
        });
        if (this.ressalvas.length === 0) {
            runInAction(() => (this.abrirDialog = false));
            this.mensagemStore.abrirMensagemAlerta(
                'Não há observações de ressalvas de votos confirmados a serem exibidos'
            );
        }
    };

    handleClickAdicionarRascunho = async (sigla, texto) => {
        texto = this.limparHtml(texto);
        let rascunho = await this.certidaoService.recuperarRascunhoCertidao(this.registro);
        if (rascunho.indexOf(texto) === -1 || this.abrirConfirmacaoAdicionarRascunhoRessalva) {
            rascunho = this.alterarRascunhoCertidao(sigla, rascunho, texto);
            await this.certidaoService.salvarRascunhoCertidao(this.registro, rascunho);
            runInAction(() => (this.registro.rascunhoCertidao = rascunho));
            this.mensagemStore.abrirMensagemSucesso('Texto da ressalva adicionado ao rascunho da certidão');
            this.abrirConfirmacaoAdicionarRascunhoRessalva = false;
        } else {
            this.abrirConfirmacaoAdicionarRascunhoRessalva = true;
        }
    };

    alterarRascunhoCertidao(sigla, rascunho, texto) {
        let magistrado = this.dominioStore.magistradosComSigla.find((mag) => mag.sigla === sigla);
        let tratamento = this.salaSessaoStore.pronomeTratamentoComCargo(magistrado);
        rascunho = this.salaSessaoStore.renumerarObservacoes(rascunho);

        return `${rascunho}\n\nObservação ${this.salaSessaoStore.getQuantidadeObservacoes(rascunho) + 1}: ${tratamento} ${magistrado.nome} registrou ressalva de entendimento pessoal.\n${texto}\n`;
    }

    limparHtml(html) {
        let tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    fecharConfirmacaoTextoRessalva = () => {
        this.abrirConfirmacaoAdicionarRascunhoRessalva = false;
    };

    renderConfirmacao(sigla, ressalva) {
        return (
            <ModalConfirmacao
                titulo={'Adicionar texto da ressalva no rascunho da certidão'}
                open={this.abrirConfirmacaoAdicionarRascunhoRessalva}
                callbackCancelar={this.fecharConfirmacaoTextoRessalva}
                callbackConfirmar={() => this.handleClickAdicionarRascunho(sigla, ressalva)}
            >
                <Typography display={'inline'}>
                    O texto da ressalva foi encontrado no rascunho da certidão. Deseja adicionar?
                </Typography>
            </ModalConfirmacao>
        );
    }

    render() {
        if (this.existeVotoComRessalva) {
            return (
                <div style={{ margin: '3px', display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                        title={this.tooltip}
                        placement="top"
                    >
                        <Announcement
                            style={{ cursor: 'pointer', fillOpacity: '0.7', fill: 'darkorange' }}
                            onClick={this.handleClickIcone}
                        />
                    </Tooltip>
                    {this.abrirDialog && (
                        <Dialog
                            onClose={this.handleCloseDialog}
                            open={this.abrirDialog}
                            maxWidth={false}
                        >
                            <DialogTitle
                                id="simple-dialog-title"
                                style={{ paddingBottom: '0px' }}
                            >
                                {'Ressalvas do ' + this.registro.processo.numeroCompleto}
                            </DialogTitle>
                            {this.ressalvas.length > 0 ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        margin: '0px 10px',
                                        overflowX: 'auto'
                                    }}
                                >
                                    {this.ressalvas.map((ressalva) => {
                                        return (
                                            <Paper
                                                key={this.nextKey}
                                                elevation={10}
                                                style={{ display: 'flex', flexDirection: 'column', margin: '10px' }}
                                            >
                                                <div style={{ margin: '10px' }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <h4 style={{ margin: '10px 0px' }}>{ressalva.sigla}</h4>
                                                        <Tooltip
                                                            title="Adicionar o texto da ressalva ao rascunho da certidão"
                                                            placement="top"
                                                        >
                                                            <IconButton
                                                                size="large"
                                                                onClick={() =>
                                                                    this.handleClickAdicionarRascunho(
                                                                        ressalva.sigla,
                                                                        ressalva.conteudo
                                                                    )
                                                                }
                                                            >
                                                                <NoteAdd style={{ color: 'black' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {this.renderConfirmacao(ressalva.sigla, ressalva.conteudo)}
                                                    </div>
                                                    <div
                                                        dangerouslySetInnerHTML={{ __html: ressalva.conteudo }}
                                                        style={{
                                                            overflowX: 'auto',
                                                            minWidth: '300px',
                                                            maxWidth: '640px',
                                                            minHeight: '60px',
                                                            maxHeight: '200px',
                                                            textAlign: 'justify'
                                                        }}
                                                    />
                                                </div>
                                            </Paper>
                                        );
                                    })}
                                </div>
                            ) : (
                                this.appStore.requisicoesPendentes && (
                                    <CircularProgress
                                        style={{ margin: '15px' }}
                                        size={30}
                                        disableShrink={true}
                                    />
                                )
                            )}
                            <DialogActions>
                                <Button
                                    onClick={this.handleCloseDialog}
                                    color="primary"
                                >
                                    Ok
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </div>
            );
        }
        return null;
    }
}

export default inject('stores')(observer(IconeRessalva));
